import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaUserPreferences1Service } from '../service';
import { GetUserPreferencesIO } from '../types';

export const getGetUserPreferencesQueryKey = (req: GetUserPreferencesIO['input']) =>
  ['UserPreferences1', 'GetUserPreferences', req] as const;
type QueryKey = ReturnType<typeof getGetUserPreferencesQueryKey>;

/**
 * A hook that returns a query for the `GetUserPreferences` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetUserPreferencesQuery = <E = unknown, D = GetUserPreferencesIO['output']>(
  req: GetUserPreferencesIO['input'],
  options?: LimitedSchemaQueryOptions<GetUserPreferencesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetUserPreferencesIO['output'], E, D, QueryKey>({
    queryKey: getGetUserPreferencesQueryKey(req),
    queryFn: () => SchemaUserPreferences1Service.GetUserPreferences(req, { ...httpOptions, skipValidation: true }),
    ...options,
    retry: 3,
  });

/**
 * A hook that returns an array of queries for the `GetUserPreferences` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetUserPreferencesQueries = <E = unknown, D = GetUserPreferencesIO['output']>(
  argsArr: {
    req: GetUserPreferencesIO['input'];
    options?: LimitedSchemaQueryOptions<GetUserPreferencesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetUserPreferencesQueryKey(req),
      queryFn: () => SchemaUserPreferences1Service.GetUserPreferences(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetUserPreferencesQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetUserPreferences` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetUserPreferences = () => {
  const queryClient = useQueryClient();

  const fetchGetUserPreferences = async <E = unknown, D = GetUserPreferencesIO['output']>(
    req: GetUserPreferencesIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetUserPreferencesIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetUserPreferencesIO['output'], E, D, QueryKey>(
      getGetUserPreferencesQueryKey(req),
      () => SchemaUserPreferences1Service.GetUserPreferences(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetUserPreferences;
};
